import { useCallback, useLayoutEffect, RefObject } from 'react';

type UseAutoResizeOptions = {
    minHeight?: number;
    maxHeight?: number;
};

/**
 * Хук для автоматического изменения размера textarea в зависимости от содержимого
 * Оптимизирован для предотвращения дергания скролла страницы
 * @param textareaRef - реф на textarea элемент
 * @param value - текущее значение textarea
 * @param options - опции для настройки минимальной и максимальной высоты, автоскролла и позиционирования
 */
export const useAutoResize = (
    textareaRef: RefObject<HTMLTextAreaElement>,
    value: string | number | readonly string[] | undefined,
    options: UseAutoResizeOptions = {},
) => {
    const { minHeight = 268, maxHeight } = options;

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }

        // Сохраняем текущую высоту для минимизации визуальных скачков
        const currentHeight = textarea.offsetHeight;

        // Временно устанавливаем высоту в текущее значение для стабильности
        textarea.style.height = `${currentHeight}px`;

        // Создаем невидимый клон для измерения высоты содержимого
        const clone = textarea.cloneNode(true) as HTMLTextAreaElement;

        // Получаем все необходимые стили за один раз
        const computedStyle = window.getComputedStyle(textarea);

        // Применяем все стили разом через cssText для минимизации reflow/repaint
        clone.style.cssText = [
            'position: absolute',
            'visibility: hidden',
            'height: auto',
            'min-height: auto',
            'max-height: none',
            'overflow: hidden',
            'resize: none',
            `box-sizing: ${textarea.style.boxSizing || 'border-box'}`,
            `width: ${computedStyle.width}`,
            `padding: ${computedStyle.padding}`,
            `border: ${computedStyle.border}`,
            `font-size: ${computedStyle.fontSize}`,
            `font-family: ${computedStyle.fontFamily}`,
            `line-height: ${computedStyle.lineHeight}`,
        ].join('; ');

        // Добавляем клон в DOM для измерения
        textarea.parentNode?.appendChild(clone);

        // Получаем высоту содержимого из клона
        const scrollHeight = clone.scrollHeight;

        // Удаляем клон
        textarea.parentNode?.removeChild(clone);

        // Применяем ограничения по высоте
        let newHeight = Math.max(scrollHeight, minHeight);
        if (maxHeight) {
            newHeight = Math.min(newHeight, maxHeight);
        }

        // Устанавливаем новую высоту только если она отличается
        if (newHeight !== currentHeight) {
            textarea.style.height = `${newHeight}px`;
        }
    }, [textareaRef, minHeight, maxHeight]);

    // Используем useLayoutEffect для синхронного обновления высоты до отрисовки
    useLayoutEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);

    // Возвращаем функцию для ручного вызова при необходимости
    return adjustHeight;
};
